// packages/activity/src/components/SceneTransitionDialog.tsx
import { ref, update } from "firebase/database";
import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import { setSceneStatus } from "../lib/scene-edits.js";

interface Props {
  open: boolean;
  tableId: string;
  activeSceneId: string | null;
  onClose: () => void;
  /** Injection seam for tests — defaults to `Date.now()` & `crypto.randomUUID()`. */
  nowFn?: () => number;
  idFn?: () => string;
}

function defaultId(): string {
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

export default function SceneTransitionDialog(props: Props): ReactElement | null {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [descKey, setDescKey] = useState("");
  const [busy, setBusy] = useState(false);

  if (!props.open) return null;

  const nowFn = props.nowFn ?? (() => Date.now());
  const idFn = props.idFn ?? defaultId;

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const { db } = getFirebase();
      const now = nowFn();
      const newId = idFn();
      const newPath = `/tables/${props.tableId}/scenes/${newId}`;
      const writes: Record<string, unknown> = {
        [newPath]: {
          status: "active",
          createdAt: now,
          activatedAt: now,
          name: trimmed,
          ...(descKey.trim() ? { descKey: descKey.trim() } : {}),
        },
      };
      if (props.activeSceneId) {
        Object.assign(
          writes,
          setSceneStatus(`/tables/${props.tableId}/scenes/${props.activeSceneId}`, "ended", {
            now,
          }),
        );
      }
      await update(ref(db), writes);
      props.onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: native <dialog> requires imperative showModal(); ARIA dialog pattern is intentional
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="scene-transition-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md space-y-3 rounded-lg bg-white p-6 shadow-xl"
      >
        <h2 id="scene-transition-title" className="text-lg font-semibold">
          {t("edit.transition.title")}
        </h2>
        {props.activeSceneId && (
          <p className="text-sm text-amber-800">{t("edit.transition.current-will-end")}</p>
        )}
        <label className="block text-sm">
          <span>{t("edit.scene.name")}</span>
          <input
            className="mt-1 w-full rounded border px-2 py-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            aria-required="true"
          />
        </label>
        <label className="block text-sm">
          <span>{t("edit.scene.desc-key")}</span>
          <input
            className="mt-1 w-full rounded border px-2 py-1"
            value={descKey}
            onChange={(e) => setDescKey(e.target.value)}
            placeholder={t("edit.scene.desc-key-placeholder")}
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="rounded border px-3 py-1 text-sm"
            onClick={props.onClose}
            disabled={busy}
          >
            {t("edit.cancel")}
          </button>
          <button
            type="submit"
            className="rounded bg-emerald-600 px-3 py-1 text-sm text-white disabled:opacity-50"
            disabled={busy || name.trim().length === 0}
          >
            {busy ? t("edit.transition.launching") : t("edit.transition.launch")}
          </button>
        </div>
      </form>
    </div>
  );
}
